/**
 * Server-Abgleich: Anmeldung, Haus öffnen, Einrichtungen und Leitungspläne laden/speichern.
 * Beide Arten gehören immer einem Nutzer (jeder sieht nur seine eigenen) und sind unabhängig voneinander:
 * eine Einrichtung löschen lässt die Leitungen unberührt und umgekehrt.
 * Ohne erreichbares Backend läuft die App im Browser-Modus (localStorage).
 */
import type { HouseKey, HouseSummary, Layout, LayoutData, LayoutKind } from "@hausplaner/model";
import { emptyLayout } from "@hausplaner/model";
import { api, ApiError } from "../api/client";
import {
  useStore, applyData, resetHist, serializeKind, toast, flushSave, touch, markSaved, retrySave,
  writeBackup, loadBackup, dropBackup, layoutKey, resetHouseState, kindsFor, KIND_LABEL, type DocSlot,
} from "./store";

const get = useStore.getState;
const set = useStore.setState;
const LAST_KEY = "hausplaner-last-layout";

// zuletzt geöffnete Einrichtung bzw. Leitungsplan je Nutzer, Haus-Zustand und Art (ein Browser kann von mehreren genutzt werden)
const lastKey = (kind: LayoutKind) => [LAST_KEY, get().me?.username || "", get().houseKey || "", kind].join(":");
function remember(kind: LayoutKind, id: string) { try { localStorage.setItem(lastKey(kind), id); } catch { /* egal */ } }
function lastOpened(kind: LayoutKind): string | null { try { return localStorage.getItem(lastKey(kind)); } catch { return null; } }

const sameJSON = (a: unknown, b: unknown) => JSON.stringify(a) === JSON.stringify(b);
const core = (kind: LayoutKind, d: LayoutData) => (kind === "einrichtung" ? [d.items || [], d.custom || []] : [d.lines || [], d.points || []]);
const patchDoc = (kind: LayoutKind, p: Partial<DocSlot>) => set({ docs: { ...get().docs, [kind]: { ...get().docs[kind], ...p } } });

/* ---------- Start ---------- */
let started: Promise<void> | null = null;
/** Anmeldung prüfen. Läuft nur einmal, auch wenn React den Effekt doppelt auslöst (StrictMode). */
export function initApp() { return (started ??= start()); }
async function start() {
  try { set({ me: await api.me(), online: true, needLogin: false, authChecked: true }); }
  catch (e) {
    if (e instanceof ApiError && e.status === 401) { set({ online: true, needLogin: true, authChecked: true }); return; }
    set({ online: false, authChecked: true });
  }
}

/** Anmelden; danach liegengebliebene Änderungen speichern */
export async function login(username: string, password: string, remember: boolean) {
  await api.login(username, password, remember);
  const me = await api.me();
  const prev = get().me;
  if (prev && prev.id !== me.id) { location.href = "/"; return; } // anderer Nutzer: frisch starten
  set({ me, online: true, needLogin: false });
  if (get().houseKey) retrySave();
}

/** Abmelden: erst speichern, dann Sitzung beenden und neu starten */
export async function logout() {
  await flushSave().catch(() => undefined);
  try { await api.logout(); } catch { /* egal */ }
  location.href = "/";
}

export async function fetchHouses(): Promise<HouseSummary[] | null> {
  try { return await api.houses(); } catch { return null; }
}

/** Sitzung abgelaufen: Anmeldung über der App zeigen; Änderungen bleiben im Browser gesichert */
export function relogin() { set({ needLogin: true }); }

/* ---------- Haus öffnen ---------- */
let opening: Promise<void> | null = null, openingKey: HouseKey | null = null;
export function openHouse(key: HouseKey) {
  if (opening && openingKey === key) return opening;
  openingKey = key;
  return (opening = (async () => {
    await initApp();
    if (get().needLogin) return; // erst anmelden – danach wird das Haus erneut geöffnet
    await flushSave();
    resetHouseState(key);
    const restore: Partial<Record<LayoutKind, LayoutData>> = {};
    try {
      for (const kind of kindsFor(key)) await loadKind(key, kind, restore);
    } catch (e) {
      if (e instanceof ApiError && e.status === 401) { relogin(); return; }
      for (const kind of kindsFor(key)) patchDoc(kind, { save: "error" });
      toast("Daten konnten nicht geladen werden: " + (e as Error).message);
    }
    resetHist();
    if (Object.keys(restore).length) set({ restore });
  })().finally(() => { opening = null; openingKey = null; }));
}

async function loadKind(key: HouseKey, kind: LayoutKind, restore: Partial<Record<LayoutKind, LayoutData>>) {
  if (!get().online) {
    const local = loadBackup<LayoutData>(layoutKey(null, key, kind));
    if (local) applyData(local, { only: kind });
    markSaved(kind);
    return;
  }
  const list = await api.listLayouts(key, kind); // nur die eigenen
  set({ lists: { ...get().lists, [kind]: list } });
  const pick = list.find((l) => l.id === lastOpened(kind)) || list[0];
  if (pick) useDoc(await api.getLayout(pick.id), restore);
  else { // erste Nutzung: eigene Einrichtung bzw. eigenen Leitungsplan anlegen
    useDoc(await api.createLayout({ house: key, kind, name: KIND_LABEL[kind].first, data: emptyLayout() }), restore);
    await refreshList(kind);
  }
}

function useDoc(l: Layout, restore?: Partial<Record<LayoutKind, LayoutData>>) {
  const kind = l.kind;
  patchDoc(kind, { id: l.id, name: l.name, version: l.version, save: "saved" });
  applyData(l.data, { only: kind });
  markSaved(kind);
  remember(kind, l.id);
  // gab es hier Änderungen, die der Server nie bekommen hat?
  const b = loadBackup<LayoutData>(layoutKey(l.id, l.house, kind));
  if (restore && b && b.synced === false && !sameJSON(core(kind, b), core(kind, serializeKind(kind)))) restore[kind] = b;
}

export async function refreshList(kind: LayoutKind) {
  if (!get().online) return;
  const key = get().houseKey; if (!key) return;
  try { set({ lists: { ...get().lists, [kind]: await api.listLayouts(key, kind) } }); } catch { /* Liste bleibt */ }
}

/* ---------- Speichern (vom Store aufgerufen) ---------- */
export async function saveDoc(kind: LayoutKind, data: LayoutData): Promise<boolean> {
  const d = get().docs[kind]; if (!d.id) return false;
  patchDoc(kind, { save: "saving" });
  try {
    const l = await api.updateLayout(d.id, { data, version: d.version });
    if (get().docs[kind].id === l.id) patchDoc(kind, { version: l.version, save: "saved" });
    return true;
  } catch (e) {
    if (e instanceof ApiError && e.status === 401) { patchDoc(kind, { save: "auth" }); relogin(); }
    else if (e instanceof ApiError && e.status === 409) { patchDoc(kind, { save: "conflict" }); toast(`${KIND_LABEL[kind].one} wurde auf einem anderen deiner Geräte geändert. Neu laden oder als Kopie speichern.`); }
    else patchDoc(kind, { save: "error" });
    return false;
  }
}

/* ---------- Wiederherstellen ---------- */
export function restoreBackup() {
  const r = get().restore; if (!r) return;
  for (const kind of Object.keys(r) as LayoutKind[]) applyData(r[kind], { only: kind });
  set({ restore: null }); touch(); toast("Änderungen aus diesem Browser wiederhergestellt");
}
export function discardBackup() {
  const s = get();
  for (const kind of Object.keys(s.restore || {}) as LayoutKind[]) dropBackup(layoutKey(s.docs[kind].id, s.houseKey, kind));
  set({ restore: null });
}

/* ---------- Einrichtungen und Leitungspläne verwalten ---------- */
export async function openDoc(kind: LayoutKind, id: string) {
  await flushSave();
  try { useDoc(await api.getLayout(id)); resetHist(); set({ menu: false }); }
  catch (e) { toast(`${KIND_LABEL[kind].one} konnte nicht geladen werden: ` + (e as Error).message); }
}

export async function createDoc(kind: LayoutKind, name: string, copyCurrent: boolean) {
  await flushSave();
  const data = copyCurrent ? serializeKind(kind) : emptyLayout();
  const house = get().houseKey || "planung";
  try { const l = await api.createLayout({ house, kind, name, data }); useDoc(l); resetHist(); await refreshList(kind); toast(`„${l.name}“ angelegt`); }
  catch (e) { toast("Anlegen fehlgeschlagen: " + (e as Error).message); }
}

export async function duplicateDoc(kind: LayoutKind, id: string, name: string) {
  await flushSave();
  try { const l = await api.duplicateLayout(id, name); useDoc(l); resetHist(); await refreshList(kind); toast(`Kopie „${l.name}“ angelegt`); }
  catch (e) { toast("Kopieren fehlgeschlagen: " + (e as Error).message); }
}

/** Konflikt lösen: die aktuelle Fassung als neue Kopie sichern */
export async function saveAsCopy(kind: LayoutKind) { await createDoc(kind, `${get().docs[kind].name} (Kopie)`, true); }

export async function renameDoc(kind: LayoutKind, id: string, name: string) {
  const l = get().lists[kind].find((x) => x.id === id); if (!l) return;
  try {
    const cur = get().docs[kind].id === id;
    const res = await api.updateLayout(id, { name, version: cur ? get().docs[kind].version : l.version });
    if (cur) patchDoc(kind, { name: res.name, version: res.version });
    await refreshList(kind);
  } catch (e) { toast("Umbenennen fehlgeschlagen: " + (e as Error).message); }
}

export async function deleteDoc(kind: LayoutKind, id: string) {
  try {
    await api.deleteLayout(id);
    dropBackup(layoutKey(id, get().houseKey, kind));
    await refreshList(kind);
    if (get().docs[kind].id === id) {
      const next = get().lists[kind][0];
      if (next) await openDoc(kind, next.id);
      else await createDoc(kind, KIND_LABEL[kind].first, false);
    }
    toast(`${KIND_LABEL[kind].one} gelöscht`);
  } catch (e) { toast("Löschen fehlgeschlagen: " + (e as Error).message); }
}

export { writeBackup };
